import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
	FrameStatus,
	DomElement,
	Frame,
	VisualEditFrameEntry,
	TransformVisualEdit,
	TextVisualEdit,
	ImageVisualEdit,
	VisualEdit,
} from 'dragonmouse-shared/interfaces';
import { createHash } from 'dragonmouse-shared/util';
import { ToStudio, SelectionInfo, Tool, ImageElement } from 'dragonmouse-shared/devigner';
import { computeViewport, Size, Viewport } from './util.ts';
import Icon, { IconType } from 'components/Icon';
import { Loader } from 'components/Widget/Loader';
import Button, { ButtonType } from 'components/Widget/Button/Button';
import { unthemed } from 'tokens';
import forge from 'apis/forge';
import * as S from './styles.ts';
import { DEFAULT_CANVAS_COLOR, selectActiveScene } from 'redux/db/reducers.ts';
import { shallowEqual, useSelector } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { frameImageUrl } from 'utils';
import { useImageLoading } from 'hooks/useImageLoading';
import { isForgeOrigin, sendToDevigner } from '../../../devigner.ts';
import { useEventListener } from 'events/EventManager';
import { Point } from 'dragonmouse-shared/geometry.ts';
import { v4 as uuid } from 'uuid';
import ImageSelectedModal from 'components/Modal/ImageSelectedModal';
import { trackVisualEditAction } from 'analytics';

interface PrototypeProps {
	frame: Frame;
	siteUrl: string | null;
	isDragging: boolean;
	activeTool: Tool | null;
	fullscreenMode: boolean;
	onSelection: (selectionInfo: SelectionInfo) => void;
	onSetActiveTool: (tool: Tool | null) => void;
	onSetSelectedElements: (elements: DomElement[]) => void;
	onIframeReadyChange: (ready: boolean) => void;
	onContextMenu?: (selectionInfo: SelectionInfo, point: Point) => void;
	prototypeFrameSize?: { width: number; height: number };
	onSetFrameSize?: (size: { width: number; height: number }) => void;
	iframeReloadKey?: number;
	activeMode?: string | null;
}

const Prototype = ({
	frame,
	siteUrl,
	isDragging,
	activeTool,
	fullscreenMode,
	onSelection,
	onSetActiveTool,
	onSetSelectedElements,
	onIframeReadyChange,
	onContextMenu,
	prototypeFrameSize,
	onSetFrameSize,
	iframeReloadKey,
	activeMode,
}: PrototypeProps) => {
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const frameRef = useRef(frame);
	const [iframeReady, setIframeReady] = useState(false);
	const [currentFrameSize, setCurrentFrameSize] = useState<Size>({
		width: prototypeFrameSize?.width ?? frame.width ?? 0,
		height: prototypeFrameSize?.height ?? frame.height ?? 0,
	});
	const [isResizing, setIsResizing] = useState(false);
	const [preservedScale, setPreservedScale] = useState<number | null>(null);
	const [containerSize, setContainerSize] = useState<Size>({ width: 0, height: 0 });
	const [viewport, setViewport] = useState<Viewport | null>(null);
	const [selectedImageElement, setSelectedImageElement] = useState<ImageElement | null>(null);
	const [isApplyingVisualEdits, setIsApplyingVisualEdits] = useState(false);

	const imageSrc = frameImageUrl(frame);
	const [isImageLoading, handleImageRef] = useImageLoading(imageSrc ?? '');

	const { scene, prototypeScaling } = useSelector((r: RootReducer) => {
		return {
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			prototypeScaling: r.mode.prototypeScaling,
		};
	}, shallowEqual);

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	const syncToolToDevigner = (siteId: string, activeTool: Tool | null) => {
		if (activeTool === 'select' || activeTool === 'text' || activeTool === 'image') {
			sendToDevigner(iframeRef.current, siteId, {
				type: 'startOverlay',
				tool: activeTool ?? undefined,
			});
		} else {
			// Stop overlay when no tool is active
			sendToDevigner(iframeRef.current, siteId, {
				type: 'stopOverlay',
			});
		}
	};

	useEffect(() => {
		if (!frame?.siteId || !iframeReady) return;
		sendToDevigner(iframeRef.current, frame.siteId, {
			type: 'setMode',
			mode: activeMode ?? '',
		});
	}, [activeMode, frame?.siteId, iframeReady]);

	// Watch container size changes
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				const { width, height } = entry.contentRect;
				setContainerSize({ width, height });
			}
		});

		resizeObserver.observe(container);

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	// Re-initialize currentFrameSize when frame changes
	useEffect(() => {
		setCurrentFrameSize({ width: frame.width, height: frame.height });
	}, [frame.width, frame.height]);

	// Sync with prototypeFrameSize prop when it changes
	useEffect(() => {
		if (prototypeFrameSize) {
			setCurrentFrameSize({ width: prototypeFrameSize.width, height: prototypeFrameSize.height });
		}
	}, [prototypeFrameSize?.width, prototypeFrameSize?.height]);

	// Recalculate viewport when dependencies change
	useEffect(() => {
		if (containerSize.width === 0 || containerSize.height === 0) return; // Wait for container to be measured

		const newViewport = computeViewport(
			currentFrameSize,
			containerSize,
			prototypeScaling,
			fullscreenMode
		);
		setViewport(newViewport);
	}, [currentFrameSize, containerSize, prototypeScaling, fullscreenMode, preservedScale]);

	// Message handler ref — updated every render so the persistent listener
	// always has access to fresh props/state without needing to be torn down.
	const messageHandlerRef = useRef<(toStudio: ToStudio) => void>(() => {});
	messageHandlerRef.current = (toStudio: ToStudio) => {
		const iframeRect = iframeRef.current?.getBoundingClientRect();
		const currentFrame = frameRef.current;
		const msgSiteId = toStudio.siteId;

		// Commit messages: always process, using the message's siteId to find the right entry.
		// These must never be dropped, even if the user switched versions since the edit began.
		if (toStudio.type === 'dragStart') {
			const transformEdit: TransformVisualEdit = {
				kind: 'transform',
				elementId: toStudio.elementId,
				transform: { x: 0, y: 0 },
			};
			addOrUpdateVisualEdit(
				transformEdit,
				(edit) => edit.elementId === toStudio.elementId,
				msgSiteId
			);
			return;
		} else if (toStudio.type === 'dragEnd') {
			const transformEdit: TransformVisualEdit = {
				kind: 'transform',
				elementId: toStudio.elementId,
				transform: toStudio.finalTransform,
			};
			addOrUpdateVisualEdit(
				transformEdit,
				(edit) => edit.elementId === toStudio.elementId,
				msgSiteId
			);
			return;
		} else if (toStudio.type === 'textEditEnd') {
			if (toStudio.finalText === toStudio.originalText) {
				trackVisualEditAction('remove_text_edit');
				removeVisualEdit(
					(edit) => edit.kind === 'text' && edit.elementId === toStudio.elementId,
					msgSiteId
				);
				return;
			}
			const textEdit: TextVisualEdit = {
				kind: 'text',
				elementId: toStudio.elementId,
				text: toStudio.finalText,
			};
			trackVisualEditAction('commit_text_edit');
			addOrUpdateVisualEdit(
				textEdit,
				(edit) => edit.kind === 'text' && edit.elementId === toStudio.elementId,
				msgSiteId
			);
			return;
		}

		// Interactive messages: only process for the current siteId
		if (msgSiteId !== currentFrame?.siteId) return;

		if (toStudio.type === 'contextMenu') {
			if (!viewport) return;
			if (!iframeRect) return;
			const point = {
				x: toStudio.point.x,
				y: toStudio.point.y,
			};
			const adjustedPoint = {
				x: iframeRect.left + point.x * viewport.scale,
				y: iframeRect.top + point.y * viewport.scale + 25,
			};
			if (toStudio.selectionInfo) {
				onContextMenu?.(toStudio.selectionInfo, adjustedPoint);
			}
		} else if (toStudio.type === 'selection') {
			onSelection(toStudio.selectionInfo);
		} else if (toStudio.type === 'ready') {
			if (currentFrame?.siteId) {
				syncToolToDevigner(currentFrame.siteId, activeTool);
			}
		} else if (toStudio.type === 'keyPress') {
			// handle key presses from within the iframe
			if (toStudio.key === 'Escape') {
				// Exit select mode (devigner handles drag cancellation internally)
				onSetActiveTool(null);
				onSetSelectedElements([]);
			}
		} else if (toStudio.type === 'devignerReady') {
			setIframeReady(true);
		} else if (toStudio.type === 'imageSelected') {
			setSelectedImageElement(toStudio.imageElement);
		}
	};

	// Persistent listener — lives for the component's lifetime, never torn down.
	// Delegates to messageHandlerRef which always has fresh values.
	useEffect(() => {
		const listener = (event: MessageEvent) => {
			if (!isForgeOrigin(event)) return;
			if (event.source !== iframeRef.current?.contentWindow) return;
			messageHandlerRef.current(event.data as ToStudio);
		};
		window.addEventListener('message', listener);
		return () => window.removeEventListener('message', listener);
	}, []);

	useEffect(() => {
		if (!siteUrl || !frame?.siteId) return;
		syncToolToDevigner(frame.siteId, activeTool);
	}, [activeTool, siteUrl, frame.siteId]);

	useEventListener('devigner-clear-selection', () => {
		if (frame?.siteId) {
			sendToDevigner(iframeRef.current, frame.siteId, { type: 'clearSelection' });
		}
	});

	useEventListener('devigner-select-element', (elementId) => {
		if (frame?.siteId) {
			sendToDevigner(iframeRef.current, frame.siteId, { type: 'selectElement', elementId });
		}
	});

	// Apply/clear visual edits on initialization events only (not on entry mutations).
	// The devigner already has the correct state after a user edit — re-applying
	// from server state would echo stale data back resulting in mayhem.
	useEffect(() => {
		applyVisualEdits(frame);
	}, [frame?.id, frame?.metadata?.visualEditActive, frame?.siteId, iframeReady, siteUrl]);

	// Iframe needs re-initialization when siteUrl changes or iframe is reloaded via key
	useEffect(() => {
		setIframeReady(false);
	}, [siteUrl, iframeReloadKey]);

	// Notify parent when iframe ready state changes
	// For images (when siteUrl is null), consider them ready when not loading
	useEffect(() => {
		if (siteUrl) {
			onIframeReadyChange(iframeReady);
		} else {
			// For images, we're ready when the image is not loading
			onIframeReadyChange(!isImageLoading);
		}
	}, [iframeReady, isImageLoading, siteUrl, onIframeReadyChange]);

	// Store current scale when view method changes
	useEffect(() => {
		if (frame && viewport) {
			// Store the current scale for preservation
			setPreservedScale(viewport?.scale);
		}
	}, [prototypeScaling, frame, viewport?.scale]);

	//------------------------------------------------------------------------------------------------
	// UTILITY
	//------------------------------------------------------------------------------------------------

	// Helper function to update frame size and notify parent
	const updateFrameSize = (newSize: { width: number; height: number }) => {
		setCurrentFrameSize(newSize);
		onSetFrameSize?.(newSize);
	};

	// Helper function to add or update a visual edit in the frame's visual edit entry.
	// siteId can be passed explicitly for commit messages that arrive after a version switch.
	const addOrUpdateVisualEdit = (
		edit: VisualEdit,
		findExistingPredicate: (existingEdit: VisualEdit) => boolean,
		siteId: string
	) => {
		const currentFrame = frameRef.current;
		if (!currentFrame) return;

		const currentEntries = currentFrame.entries || [];
		const visualEditIndex = currentEntries.findIndex(
			(e): e is VisualEditFrameEntry =>
				e.kind === 'visualEdit' && 'siteId' in e && e.siteId === siteId
		);

		if (visualEditIndex >= 0) {
			// Update existing visual edit entry
			const visualEditEntry = currentEntries[visualEditIndex] as VisualEditFrameEntry;
			const existingEditIndex = visualEditEntry.visualEdits.findIndex(findExistingPredicate);

			const updatedVisualEdits = [...visualEditEntry.visualEdits];
			if (existingEditIndex >= 0) {
				updatedVisualEdits[existingEditIndex] = edit;
			} else {
				updatedVisualEdits.push(edit);
			}

			const updatedEntry: VisualEditFrameEntry = {
				...visualEditEntry,
				visualEdits: updatedVisualEdits,
				hash: createHash(updatedVisualEdits) || undefined,
			};

			forge.updateFrameEntry({ frameId: currentFrame.id, entry: updatedEntry });
		} else {
			// Create new visual edit entry
			const newEntry: VisualEditFrameEntry = {
				id: uuid(),
				kind: 'visualEdit',
				createdAt: new Date(),
				deletedAt: null,
				siteId: siteId,
				visualEdits: [edit],
				hash: createHash([edit]) || undefined,
			};

			forge.addFrameEntry({ frameId: currentFrame.id, entry: newEntry });
			// Only flip visualEditActive if this edit is for the current site
			if (siteId === currentFrame.siteId) {
				forge.updateFrame({
					update: {
						id: currentFrame.id,
						metadata: { ...currentFrame.metadata, visualEditActive: true },
					},
				});
			}
		}
	};

	const handleImageSubmit = (imageUrl: string, altTag: string) => {
		if (!selectedImageElement || !frame.siteId) return;

		const imageEdit: ImageVisualEdit = {
			kind: 'image',
			elementId: selectedImageElement.elementId,
			imageUrl: imageUrl,
			altTag: altTag,
		};

		// Save to server (optimistic update happens in Redux)
		// applyVisualEdits will handle fetching signed URLs when frame.entries updates
		trackVisualEditAction('commit_image_edit');
		addOrUpdateVisualEdit(
			imageEdit,
			(edit) => edit.kind === 'image' && edit.elementId === selectedImageElement.elementId,
			frame.siteId
		);

		setSelectedImageElement(null);
	};

	// Convention: Visual edit entries should be deleted when they become empty.
	// This matches the behavior in VisualEditEntry.onDelete which also deletes the entire entry.
	const removeVisualEdit = (
		findExistingPredicate: (existingEdit: VisualEdit) => boolean,
		siteId: string
	) => {
		const currentFrame = frameRef.current;
		if (!currentFrame) return;

		const currentEntries = currentFrame.entries || [];
		const visualEditIndex = currentEntries.findIndex(
			(e): e is VisualEditFrameEntry =>
				e.kind === 'visualEdit' && 'siteId' in e && e.siteId === siteId
		);

		if (visualEditIndex >= 0) {
			const visualEditEntry = currentEntries[visualEditIndex] as VisualEditFrameEntry;
			const existingEditIndex = visualEditEntry.visualEdits.findIndex(findExistingPredicate);

			if (existingEditIndex >= 0) {
				const updatedVisualEdits = [...visualEditEntry.visualEdits];
				updatedVisualEdits.splice(existingEditIndex, 1);

				// If no visual edits remain, delete the entry entirely
				if (updatedVisualEdits.length === 0) {
					forge.deleteFrameEntry({ frameId: currentFrame.id, entryId: visualEditEntry.id });

					// Also disable visual edit mode if this was the active edit
					if (currentFrame.metadata.visualEditActive) {
						forge.updateFrame({
							update: {
								id: currentFrame.id,
								metadata: { ...currentFrame.metadata, visualEditActive: false },
							},
						});
					}
				} else {
					const updatedEntry: VisualEditFrameEntry = {
						...visualEditEntry,
						visualEdits: updatedVisualEdits,
						hash: createHash(updatedVisualEdits) || undefined,
					};

					forge.updateFrameEntry({ frameId: currentFrame.id, entry: updatedEntry });
				}
			}
		}
	};

	const applyVisualEdits = useCallback(
		async (currentFrame: Frame) => {
			if (!siteUrl || !currentFrame?.siteId || !iframeReady) return;

			const visualEditActive = currentFrame.metadata?.visualEditActive;

			if (visualEditActive) {
				const visualEditEntry = currentFrame.entries?.find(
					(e): e is VisualEditFrameEntry =>
						e.kind === 'visualEdit' && 'siteId' in e && e.siteId === currentFrame.siteId
				);

				if (visualEditEntry && visualEditEntry.visualEdits.length > 0 && currentFrame.id) {
					// Check if we need to fetch signed URLs (only for image edits with storage paths)
					// This is a bit fragile (the parts of a visual edit that have a storage path may change in the future)
					// But for now it spares us a server round trip when there are no storage paths.
					const hasImageEditsWithStoragePaths = visualEditEntry.visualEdits.some(
						(edit) => edit.kind === 'image' && edit.imageUrl && !edit.imageUrl.match(/^https?:\/\//)
					);

					// if we don't have any image edits, apply the edits directly
					if (!hasImageEditsWithStoragePaths) {
						sendToDevigner(iframeRef.current, currentFrame.siteId, {
							type: 'applyVisualEdits',
							visualEdits: visualEditEntry.visualEdits,
						});
						return; // no need to continue
					}

					// if we have image edits, do a round trip to the server to get the signed URLs
					setIsApplyingVisualEdits(true);
					try {
						const response = await forge.getFrameSignedUrls({
							frameId: currentFrame.id,
							siteId: currentFrame.siteId,
						});
						const urlMap = response.urlMap;

						// Apply URL map to convert storage paths to signed URLs
						const convertedEdits = visualEditEntry.visualEdits.map((edit) => {
							if (edit.kind === 'image') {
								const imageEdit = edit as ImageVisualEdit;
								// If storage path exists in map, use signed URL; otherwise use original
								const signedUrl = urlMap[imageEdit.imageUrl];
								if (signedUrl) {
									return {
										...imageEdit,
										imageUrl: signedUrl,
									} as ImageVisualEdit;
								}
							}
							return edit;
						});
						sendToDevigner(iframeRef.current, currentFrame.siteId, {
							type: 'applyVisualEdits',
							visualEdits: convertedEdits,
						});
					} catch (error) {
						console.error('Failed to get signed URLs:', error);
						Sentry.captureException(error);
						// Continue with original edits if map fetch fails
						sendToDevigner(iframeRef.current, currentFrame.siteId, {
							type: 'applyVisualEdits',
							visualEdits: visualEditEntry.visualEdits,
						});
					} finally {
						setIsApplyingVisualEdits(false);
					}
				}
			} else {
				sendToDevigner(iframeRef.current, currentFrame.siteId, {
					type: 'clearVisualEdits',
				});
			}
		},
		[siteUrl, iframeReady]
	);

	useEffect(() => {
		frameRef.current = frame;
	}, [frame]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	// Handle resize logic
	const onResizeStart = (e: React.MouseEvent, side: 'left' | 'right') => {
		e.preventDefault();
		setIsResizing(true);

		const startX = e.clientX;
		const startWidth = currentFrameSize.width;

		const handleMouseMove = (e: MouseEvent) => {
			const deltaX = e.clientX - startX;
			const scale = viewport?.scale;
			if (!scale) return;
			const deltaWidth = deltaX / scale;

			let newWidth = startWidth;
			if (side === 'right') {
				newWidth = Math.max(200, startWidth + deltaWidth);
			} else {
				newWidth = Math.max(200, startWidth - deltaWidth);
			}

			updateFrameSize({ width: newWidth, height: currentFrameSize.height });
		};

		const handleMouseUp = () => {
			setIsResizing(false);
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
	};

	const onResetFrameAfterError = async () => {
		await forge.updateFrame(
			{
				update: {
					id: frame.id,
					metadata: {
						...frame.metadata,
						status: FrameStatus.READY,
						statusMessage: '',
					},
				},
			},
			true
		);
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const renderStatusOverlay = () => {
		if (!frame.metadata.status || !viewport) return null;

		const frameReady = frame.metadata.status === FrameStatus.READY;
		if (frameReady && !isApplyingVisualEdits) return null;

		return (
			<S.FrameStatusOverlay
				x={viewport.x}
				y={viewport.y}
				width={viewport.width}
				height={viewport.height}
				scale={viewport.scale}
			>
				<S.FrameStatusContent>
					<S.ScaledStatusIndicator scale={viewport.scale}>
						{frame.metadata.status === FrameStatus.ERROR && (
							<Icon type={IconType.ERROR} width={48} height={48} color={unthemed.gray[100]} />
						)}
						{(frame.metadata.status === FrameStatus.PROCESSING || isApplyingVisualEdits) && (
							<Loader size={48} />
						)}
					</S.ScaledStatusIndicator>
					{!frameReady && (
						<S.ScaledOverlayText scale={viewport.scale}>
							{frame.metadata.statusMessage || ''}
						</S.ScaledOverlayText>
					)}
					{frame.metadata.status === FrameStatus.ERROR && (
						<S.ScaledButton scale={viewport.scale}>
							<Button onClick={onResetFrameAfterError} label="Revert" type={ButtonType.SECONDARY} />
						</S.ScaledButton>
					)}
				</S.FrameStatusContent>
			</S.FrameStatusOverlay>
		);
	};

	const canvasColor = scene?.metadata?.canvasColor ?? DEFAULT_CANVAS_COLOR;

	// Get safe background color - handle CSS variables that don't work in this context
	const safeBackgroundColor = frame.metadata.backgroundColor?.startsWith('var(')
		? undefined // this will trigger the default when passed to FrameContainer
		: frame.metadata.backgroundColor;

	return (
		<S.PrototypeContainer ref={containerRef} backgroundColor={canvasColor}>
			{viewport && (siteUrl || imageSrc) && (
				<S.FrameContainer
					x={viewport.x}
					y={viewport.y}
					width={viewport.width}
					height={viewport.height}
					scale={viewport.scale}
					backgroundColor={safeBackgroundColor}
				>
					{siteUrl ? (
						<iframe
							key={iframeReloadKey}
							ref={iframeRef}
							src={siteUrl}
							title="Frame Prototype"
							style={{
								width: '100%',
								height: '100%',
								border: 'none',
								pointerEvents: isDragging || isResizing ? 'none' : 'auto',
							}}
						/>
					) : imageSrc ? (
						<img
							key={imageSrc}
							ref={handleImageRef}
							src={imageSrc}
							alt={frame.title || 'Frame'}
							style={{
								width: '100%',
								height: '100%',
								objectFit: 'contain',
								pointerEvents: isDragging || isResizing ? 'none' : 'auto',
							}}
						/>
					) : null}
				</S.FrameContainer>
			)}
			{renderStatusOverlay()}
			{!fullscreenMode && viewport && (siteUrl || imageSrc) && (
				<>
					{viewport.x > 20 && (
						<S.ResizeHandle
							side="left"
							x={viewport.x - 2}
							y={viewport.y}
							height={viewport.height * viewport.scale}
							onMouseDown={(e) => onResizeStart(e, 'left')}
						/>
					)}
					{containerSize.width - (viewport.x + viewport.width * viewport.scale) > 20 && (
						<S.ResizeHandle
							side="right"
							x={viewport.x + viewport.width * viewport.scale - 2}
							y={viewport.y}
							height={viewport.height * viewport.scale}
							onMouseDown={(e) => onResizeStart(e, 'right')}
						/>
					)}
				</>
			)}
			{selectedImageElement && (
				<ImageSelectedModal
					imageElement={selectedImageElement}
					onSubmit={handleImageSubmit}
					onCancel={() => setSelectedImageElement(null)}
				/>
			)}
		</S.PrototypeContainer>
	);
};

export default Prototype;
